import { useCallback, useEffect, useState } from 'react';
import type { ClassNode } from '../../../shared/catalog/classes';
import { STYLES, STYLE_CODE, STYLE_DEFS, styleUnlocked, type Mastery, type StyleId } from '../../../shared/catalog/variants';
import { apiGet, apiPost } from '../game/api';
import { audio } from '../game/audio';
import { t, type Key } from '../i18n';
import { className, classDesc, familyName } from '../i18n/catalog';
import { Icon } from './icons';
import { TankImage, TankStageView } from './TankPreview';

export interface VariantData {
  mastery: Record<string, Mastery & { best: number }>;
  choices: Record<string, StyleId>;
}

/** État des variantes du compte (null : invité ou non chargé) et choix d'une variante. */
export function useVariants(enabled: boolean) {
  const [data, setData] = useState<VariantData | null>(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (!enabled) {
      setData(null);
      return;
    }
    void apiGet<VariantData>('/api/variants')
      .then(setData)
      .catch(() => setData(null));
  }, [enabled]);
  const choose = useCallback(async (classId: string, style: StyleId) => {
    const r = await apiPost<{ result?: string }>('/api/variants/choose', { classId, style });
    if (r.data.result === 'ok') {
      setData((d) => (d ? { ...d, choices: { ...d.choices, [classId]: style } } : d));
      setMsg(t('variantChosen', { style: t(`base_${style}` as Key) }));
      audio.play('uiClick');
    } else {
      setMsg(t('variantLockedMsg'));
      audio.play('uiDeny');
    }
  }, []);
  return { data, msg, choose, clearMsg: () => setMsg('') };
}

/** Ce qu'il manque pour débloquer un style, en clair. */
export function needText(style: StyleId, m: Mastery | undefined): string {
  const need = STYLE_DEFS[style].unlock;
  const parts: string[] = [];
  if (need.seconds) parts.push(t('variantNeedTime', { have: Math.floor((m?.seconds ?? 0) / 60), need: need.seconds / 60 }));
  if (need.kills) parts.push(t('variantNeedKills', { have: m?.kills ?? 0, need: need.kills }));
  return parts.join(' · ');
}

/** Part de la maîtrise déjà acquise pour un style (0 à 1), pour la jauge sous la vignette. */
function needProgress(style: StyleId, m: Mastery | undefined): number {
  const need = STYLE_DEFS[style].unlock;
  const parts: number[] = [];
  if (need.seconds) parts.push(Math.min(1, (m?.seconds ?? 0) / need.seconds));
  if (need.kills) parts.push(Math.min(1, (m?.kills ?? 0) / need.kills));
  return parts.length ? Math.min(...parts) : 1;
}

/**
 * Fiche d'une classe : grand aperçu animé dans le style regardé, les cinq styles avec leur progression,
 * et le choix du style utilisé en partie (compte connecté, style débloqué).
 */
export function VariantPanel({ cls, variants }: { cls: ClassNode; variants: ReturnType<typeof useVariants> }) {
  const data = variants.data;
  const m = data?.mastery[cls.id];
  const chosen = data?.choices[cls.id] ?? 'atelier';
  const [look, setLook] = useState<StyleId>(chosen);
  useEffect(() => {
    setLook(chosen);
    variants.clearMsg();
  }, [cls.id, chosen]);
  const isBase = cls.id === 'BASE';
  const open = styleUnlocked(look, m);
  return (
    <section className="plate variant-panel" data-testid="variant-preview" aria-labelledby="variant-title">
      <div className="variant-stage">
        <TankStageView cls={cls} look={{ code: STYLE_CODE[look] }} radius={60} label={t('hangarStageLabel', { name: className(cls) })} />
      </div>
      <div className="variant-info">
        <span className="eyebrow">
          {familyName(cls.family)} · {t('level', { n: cls.level })} · <span className="num">{cls.id}</span>
        </span>
        <h2 className="display" id="variant-title">
          {className(cls)}
        </h2>
        <p className="variant-desc">{classDesc(cls)}</p>
        <span className={`badge ${cls.status === 'implemented' ? 'badge-ok' : ''}`}>{cls.status === 'implemented' ? t('implemented') : t('planned')}</span>
        {data && m && <p className="wip">{t('variantMastery', { min: Math.floor(m.seconds / 60), kills: m.kills })}</p>}
      </div>
      {!isBase && (
        <>
          <div className="variant-styles" role="group" aria-label={t('variantsTitle')}>
            {STYLES.map((s) => {
              const unlocked = !data || styleUnlocked(s, m);
              const prog = needProgress(s, m);
              return (
                <button key={s} type="button" className={`variant-style ${look === s ? 'on' : ''} ${data && !unlocked ? 'locked' : ''} ${chosen === s && data ? 'chosen' : ''}`} aria-pressed={look === s} onClick={() => setLook(s)}>
                  <TankImage cls={cls} look={{ code: STYLE_CODE[s] }} size={52} />
                  <span className="variant-style-name">{t(`base_${s}` as Key)}</span>
                  {data && !unlocked && (
                    <>
                      <span className="variant-lock" aria-hidden>
                        <Icon name="lock" size={12} />
                      </span>
                      <span className="variant-need">{needText(s, m)}</span>
                      <span className="mini-gauge" aria-hidden>
                        <i style={{ width: `${prog * 100}%` }} />
                      </span>
                    </>
                  )}
                  {data && chosen === s && (
                    <span className="variant-chosen" aria-hidden>
                      <Icon name="check" size={12} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {data ? (
            <div className="variant-actions">
              {chosen === look ? (
                <span className="badge badge-ok">
                  <Icon name="check" size={12} />
                  {t('variantInUse')}
                </span>
              ) : (
                <button type="button" className="btn btn-small" disabled={!open} onClick={() => void variants.choose(cls.id, look)}>
                  {open ? t('variantUse', { style: t(`base_${look}` as Key) }) : needText(look, m)}
                </button>
              )}
              {variants.msg && <span className="wip">{variants.msg}</span>}
            </div>
          ) : (
            <p className="wip">{t('variantsLogin')}</p>
          )}
        </>
      )}
    </section>
  );
}
